"use client";

/**
 * Storefront portion picker for a menu item (Regular / Large, Glass / Jug…).
 * Mirrors ElectronicsVariantSelector's contract: the parent adds to the cart
 * with the canonical variantId; the server resolves the price from it.
 */

import { useEffect, useState } from "react";
import { Minus, Plus, X } from "lucide-react";
import { toast } from "@/lib/toast";
import { FOOD_PLACEHOLDER_IMAGE, foodPortions, isPackPortions, portionVariantId } from "@/lib/config/food";

interface FoodPortionSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  product: any;
  onConfirm: (
    quantity: number,
    variantId: string | undefined,
    selectedVariants: Record<string, any> | undefined,
  ) => Promise<void>;
}

const money = (n: number) => `UGX ${Math.round(n).toLocaleString()}`;

export function FoodPortionSelector({ isOpen, onClose, product, onConfirm }: FoodPortionSelectorProps) {
  const portions = foodPortions(product);
  // Pack portions (bottle / crate) share one stock and carry no variant of their own.
  const packMode = isPackPortions(product);
  const [idx, setIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIdx(0);
      setQty(1);
    }
  }, [isOpen]);

  // Escape closes the picker, like every other dialog on the site.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  const portion = portions[idx];
  const image = product.images?.[0] || product.imageUrl || FOOD_PLACEHOLDER_IMAGE;

  const confirm = async () => {
    if (!portion) return;
    setAdding(true);
    try {
      await onConfirm(qty, packMode ? undefined : portionVariantId(portion.name), { size: portion.name });
      onClose();
    } catch (err: any) {
      toast.error(err?.message || "Failed to add to cart");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl">
        <button onClick={onClose} className="absolute right-3 top-3 rounded-full p-1.5 text-gray-500 hover:bg-gray-100" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-3">
          <img src={image} alt={product.name} className="h-16 w-16 rounded-xl object-cover" />
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-gray-900">{product.name}</h3>
            <p className="text-xs text-gray-500">Choose a portion</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          {portions.map((p, i) => (
            <button
              key={p.name}
              type="button"
              onClick={() => setIdx(i)}
              className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
                i === idx ? "border-black bg-black text-white" : "border-gray-200 bg-white text-gray-900 hover:border-gray-400"
              }`}
            >
              <p className="text-sm font-semibold">{p.name}</p>
              <p className={`text-xs ${i === idx ? "text-white/80" : "text-gray-500"}`}>
                {money(p.price)}{packMode && Number(p.units) > 1 ? ` · ${p.units} in the pack` : ""}
              </p>
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2">
          <span className="text-sm text-gray-600">Quantity</span>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="rounded-full bg-gray-100 p-1.5" aria-label="Less">
              <Minus className="h-4 w-4" />
            </button>
            <span className="w-6 text-center text-sm font-semibold">{qty}</span>
            <button type="button" onClick={() => setQty((q) => q + 1)} className="rounded-full bg-gray-100 p-1.5" aria-label="More">
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={confirm}
          disabled={adding || !portion}
          className="mt-4 w-full rounded-xl bg-black py-3 text-sm font-semibold text-white transition-opacity disabled:opacity-60"
        >
          {adding ? "Adding…" : `Add ${qty} · ${money((portion?.price || 0) * qty)}`}
        </button>
      </div>
    </div>
  );
}
